import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";
import { Branch, BranchFormData, AvailableManager } from "./branch";

class BranchService {
  // Get all branches with pagination and filtering
  async getBranches(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<ApiResponse<{ branches: Branch[]; pagination: any }>> {
    const response = await enhancedApiClient.get("/branches", { 
      params,
      context: "Fetch Branches",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branch by ID
  async getBranch(
    branchId: string
  ): Promise<ApiResponse<Branch>> {
    const response = await enhancedApiClient.get(`/branches/${branchId}`, {
      context: "Fetch Branch By ID",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Create new branch
  async createBranch(
    branchData: BranchFormData
  ): Promise<ApiResponse<Branch>> {
    const response = await enhancedApiClient.post("/branches", branchData, {
      context: "Create Branch",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update branch
  async updateBranch(
    branchId: string,
    branchData: Partial<BranchFormData>
  ): Promise<ApiResponse<Branch>> {
    // Convert boolean status to string format expected by backend
    const processedData: any = { ...branchData };
    if (typeof processedData.status === 'boolean') {
      processedData.status = processedData.status ? 'active' : 'inactive';
    }

    const response = await enhancedApiClient.put(`/branches/${branchId}`, processedData, {
      context: "Update Branch",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Delete branch
  async deleteBranch(
    branchId: string
  ): Promise<ApiResponse<{ message: string }>> {
    const response = await enhancedApiClient.delete(`/branches/${branchId}`, {
      context: "Delete Branch",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branches by status
  async getBranchesByStatus(
    status: string
  ): Promise<ApiResponse<Branch[]>> {
    const response = await enhancedApiClient.get(`/branches?status=${status}`, {
      context: "Get Branches By Status",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branches by city
  async getBranchesByCity(
    city: string
  ): Promise<ApiResponse<Branch[]>> {
    const response = await enhancedApiClient.get(`/branches?city=${city}`, {
      context: "Get Branches By City",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update branch status
  async updateBranchStatus(
    branchId: string,
    status: string
  ): Promise<ApiResponse<Branch>> {
    const response = await enhancedApiClient.patch(`/branches/${branchId}/status`, {
      status,
    }, {
      context: "Update Branch Status",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Assign manager to branch
  async assignManager(
    branchId: string,
    managerId: string
  ): Promise<ApiResponse<Branch>> {
    const response = await enhancedApiClient.patch(`/branches/${branchId}/manager`, {
      managerId,
    }, {
      context: "Assign Manager to Branch",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branch statistics
  async getBranchStats(
    branchId: string
  ): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/branches/${branchId}/stats`, {
      context: "Get Branch Statistics",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branch orders
  async getBranchOrders(
    branchId: string
  ): Promise<ApiResponse<any[]>> {
    const response = await enhancedApiClient.get(`/branches/${branchId}/orders`, {
      context: "Get Branch Orders",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get branch users
  async getBranchUsers(
    branchId: string
  ): Promise<ApiResponse<any[]>> {
    const response = await enhancedApiClient.get(`/branches/${branchId}/users`, {
      context: "Get Branch Users",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get available managers for assignment
  async getAvailableManagers(params?: {
    search?: string;
  }): Promise<ApiResponse<AvailableManager[]>> {
    const response = await enhancedApiClient.get("/branches/available-managers", {
      params,
      context: "Get Available Managers",
      component: "BranchService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }
}

export const branchService = new BranchService();
